import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, SubscriptionPackage, SubscriptionRecord, loadSession } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

function statusPill(s: SubscriptionRecord["status"]) {
  if (s === "active")       return <span className="pill pill-ok">Active</span>;
  if (s === "trialing")     return <span className="pill pill-info">Trialing</span>;
  if (s === "grace-period") return <span className="pill pill-warn">Grace period</span>;
  if (s === "past-due")     return <span className="pill pill-err">Past due</span>;
  return <span className="pill pill-muted">{s}</span>;
}

function priceLabel(pkg: SubscriptionPackage): string {
  if (pkg.waived || pkg.price === 0) return "Free";
  return `${pkg.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function featureList(pkg: SubscriptionPackage): string[] {
  return Object.entries(pkg.features).filter(([, v]) => v).map(([k]) => k);
}

export default function Subscriptions() {
  const session = loadSession();
  const isAdmin = session?.role === "admin";

  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [subs, setSubs] = useState<SubscriptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.subscriptionPackages(), api.subscription()])
      .then(([pk, s]) => {
        setPackages(pk.packages ?? []);
        setSubs(s.subscriptions ?? []);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const plansTable = useTableState(packages, {
    searchFields: ["id", "name", "description"],
    initialPageSize: 25
  });
  const subsTable = useTableState(subs, {
    searchFields: ["id", "workspaceId", "planId", "status", "billingEmail", "paymentMethodLabel"],
    initialPageSize: 25
  });

  const onDelete = async (id: string) => {
    if (!confirm(`Delete subscription package "${id}"? Workspaces on this plan will need reassignment.`)) return;
    try {
      await api.deleteSubscriptionPackage(id);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Subscriptions</h1>
          <p>Plans, entitlements, and active tenant subscriptions.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={refresh} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="section-head">
          <h2>Plans</h2>
          <span className="soft">{packages.length} configured</span>
        </div>
        {loading ? <SkeletonTable rows={5} columns={4} /> :
         packages.length === 0 ? <div className="empty">No plans configured.</div> : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={plansTable.search}
              setSearch={plansTable.setSearch}
              page={plansTable.page}
              setPage={plansTable.setPage}
              pageSize={plansTable.pageSize}
              setPageSize={plansTable.setPageSize}
              totalPages={plansTable.totalPages}
              totalRows={plansTable.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Features</th>
                  <th>Rate limits</th>
                  <th>Description</th>
                  {isAdmin && <th className="actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {plansTable.paged.map((p) => (
                  <tr key={p.id}>
                    <td><code>{p.id}</code></td>
                    <td><strong>{p.name}</strong></td>
                    <td>{priceLabel(p)}</td>
                    <td>
                      {featureList(p).length > 0
                        ? featureList(p).map((f) => <span key={f} className="pill pill-info" style={{ marginRight: 4 }}>{f}</span>)
                        : <span className="soft">—</span>}
                    </td>
                    <td>
                      {Object.entries(p.rateLimits).length === 0
                        ? <span className="soft">—</span>
                        : Object.entries(p.rateLimits).map(([k, v]) => (
                            <div key={k} className="mono" style={{ fontSize: 12 }}>{k}: {v.toLocaleString()}</div>
                          ))}
                    </td>
                    <td className="soft truncate" title={p.description}>{p.description ?? "—"}</td>
                    {isAdmin && (
                      <td className="actions">
                        <button className="btn btn-sm btn-danger" onClick={() => void onDelete(p.id)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Active subscriptions</h2>
          <span className="soft">{subs.length} total</span>
        </div>
        {loading ? <SkeletonTable rows={5} columns={4} /> :
         subs.length === 0 ? <div className="empty">No subscriptions in this scope.</div> : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={subsTable.search}
              setSearch={subsTable.setSearch}
              page={subsTable.page}
              setPage={subsTable.setPage}
              pageSize={subsTable.pageSize}
              setPageSize={subsTable.setPageSize}
              totalPages={subsTable.totalPages}
              totalRows={subsTable.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Workspace</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Renews</th>
                  <th>Billing</th>
                  <th>Seats</th>
                  <th>Datasets</th>
                  <th>API calls / mo</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subsTable.paged.map((s) => (
                  <tr key={s.id}>
                    <td><code>{s.id}</code></td>
                    <td><code>{s.workspaceId}</code></td>
                    <td>
                      <select
                        value={s.planId}
                        onChange={async (e) => {
                          const next = e.target.value;
                          if (next === s.planId) return;
                          try {
                            const { subscription } = await api.changeSubscriptionPlan(s.id, next);
                            setSubs((prev) => prev.map((row) => (row.id === s.id ? subscription : row)));
                          } catch (err) {
                            setError(err instanceof ApiError ? err.message : String(err));
                          }
                        }}
                      >
                        {packages.map((p) => (
                          <option key={p.id} value={p.id}>{p.id}</option>
                        ))}
                      </select>
                    </td>
                    <td>{statusPill(s.status)}</td>
                    <td className="soft">{s.renewalDate ? new Date(s.renewalDate).toLocaleDateString() : "—"}</td>
                    <td className="soft">{s.paymentMethodLabel} · {s.billingEmail}</td>
                    <td>{s.usage.seats.toLocaleString()}</td>
                    <td>{s.usage.datasets.toLocaleString()}</td>
                    <td>{s.usage.apiCallsThisMonth.toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ color: "#b91c1c" }}
                        onClick={async () => {
                          if (!confirm(`Cancel subscription "${s.id}"? The workspace loses billed entitlements.`)) return;
                          try {
                            await api.cancelSubscription(s.id);
                            setSubs((prev) => prev.filter((row) => row.id !== s.id));
                          } catch (err) {
                            setError(err instanceof ApiError ? err.message : String(err));
                          }
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>
    </div>
  );
}
